import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMatches } from '../hooks/useMatches';
import { useAuth } from '../context/AuthContext';
import MatchCard from '../components/MatchCard';

const favoriteStorageKey = 'tahminarena-live-favorites';
const LIVE_PLAYABLE_MIN_SCORE = 75;
const LIVE_PLAYABLE_MIN_DATA_QUALITY = 58;
const majorLeagueIds = new Set([2, 3, 39, 61, 71, 78, 88, 94, 128, 135, 140, 144, 203, 207, 210, 253, 307, 848]);

const hasAutomaticLiveAnalysis = (match) =>
  Boolean(
    match?.liveAnalysisAccessible &&
    (
      Number(
        match?.predictionContext?.liveInsightScore ||
        match?.predictionContext?.liveInsights?.highlights?.liveConfidenceScore ||
        0
      ) > 0 ||
      match?.liveInsights?.insights?.length > 0 ||
      match?.predictionContext?.liveInsights?.insights?.length > 0
    )
  );

const hasGoalSignalPriority = (match) => {
  const liveInsights = match?.liveInsights || match?.predictionContext?.liveInsights;
  const recommendation = liveInsights?.highlights?.recommendation || null;
  const status = String(recommendation?.status || '').trim().toLowerCase();
  const selection = String(recommendation?.selection || '').trim().toLowerCase();
  const insights = Array.isArray(liveInsights?.insights) ? liveInsights.insights : [];

  return (
    status === 'strong' ||
    selection.includes('gol') ||
    insights.some((item) => String(item?.label || '').trim().toLowerCase().includes('gol'))
  );
};

const sortAutomaticLiveAnalysisFirst = (matches = []) =>
  [...matches].sort((a, b) => {
    const goalSignalDiff = Number(hasGoalSignalPriority(b)) - Number(hasGoalSignalPriority(a));
    if (goalSignalDiff !== 0) return goalSignalDiff;

    const automaticDiff = Number(hasAutomaticLiveAnalysis(b)) - Number(hasAutomaticLiveAnalysis(a));
    if (automaticDiff !== 0) return automaticDiff;

    const insightDiff =
      Number(
        b?.predictionContext?.liveInsightScore ||
        b?.predictionContext?.liveInsights?.highlights?.liveConfidenceScore ||
        0
      ) -
      Number(
        a?.predictionContext?.liveInsightScore ||
        a?.predictionContext?.liveInsights?.highlights?.liveConfidenceScore ||
        0
      );

    if (insightDiff !== 0) return insightDiff;

    return Number(b?.status?.elapsed || 0) - Number(a?.status?.elapsed || 0);
  });

const isStrictPlayableLiveMatch = (match) => {
  const liveInsightScore = Number(
    match?.predictionContext?.liveInsightScore ||
    match?.predictionContext?.liveInsights?.highlights?.liveConfidenceScore ||
    0
  );
  const liveDataQuality = Number(match?.predictionContext?.liveDataQuality || 0);
  const leagueId = Number(match?.league?.id || 0);

  return (
    majorLeagueIds.has(leagueId) &&
    liveInsightScore >= LIVE_PLAYABLE_MIN_SCORE &&
    liveDataQuality >= LIVE_PLAYABLE_MIN_DATA_QUALITY
  );
};



const getLiveInsights = (match) => match?.liveInsights || match?.predictionContext?.liveInsights || null;

const getLiveMomentumScore = (match) => Number(
  match?.liveAnalysis?.momentum ||
  getLiveInsights(match)?.highlights?.momentum?.score ||
  getLiveInsights(match)?.highlights?.recentWindow?.momentumLast10?.score ||
  0
);

const getLiveSignalScore = (match) => Number(
  getLiveInsights(match)?.highlights?.recommendation?.signalScore ||
  getLiveInsights(match)?.highlights?.liveConfidenceScore ||
  match?.predictionContext?.liveInsightScore ||
  0
);

const getDangerousAttackScore = (match) => {
  const context = getLiveInsights(match)?.highlights?.liveContext || {};
  const home = Number(context?.dangerousAttacks?.home || 0);
  const away = Number(context?.dangerousAttacks?.away || 0);
  return Math.max(home, away);
};

const getLivePriorityScore = (match) => {
  const goalSignal = hasGoalSignalPriority(match) || Boolean(match?.liveAnalysis?.goalSignal);
  const momentum = getLiveMomentumScore(match);
  const signalScore = getLiveSignalScore(match);
  const dangerous = getDangerousAttackScore(match);
  const majorLeagueBonus = majorLeagueIds.has(Number(match?.league?.id || 0)) ? 18 : 0;
  const minuteBonus = Math.min(Number(match?.status?.elapsed || 0), 90) * 0.18;

  return (
    (goalSignal ? 1000 : 0) +
    (signalScore * 5) +
    (momentum * 3) +
    (dangerous * 0.6) +
    majorLeagueBonus +
    minuteBonus
  );
};

const sortLiveTerminalPriority = (matches = []) =>
  [...matches].sort((a, b) => getLivePriorityScore(b) - getLivePriorityScore(a));

const LiveMatches = () => {
  const [favoriteIds, setFavoriteIds] = useState([]);
  const [highlightedFixtureId, setHighlightedFixtureId] = useState('');
  const { isAdmin } = useAuth();
  const {
    liveMatches,
    loading,
    refreshingLive,
    liveNotice,
    liveWarning,
    liveDebug
  } = useMatches(favoriteIds);

  useEffect(() => {
    try {
      const stored = JSON.parse(window.localStorage.getItem(favoriteStorageKey) || '[]');
      if (Array.isArray(stored)) {
        setFavoriteIds(stored);
      }
    } catch (error) {
      setFavoriteIds([]);
    }
  }, []);

  const liveMatchIds = useMemo(
    () => new Set(liveMatches.map((match) => String(match.fixtureId || match.id || match._id))),
    [liveMatches]
  );

  useEffect(() => {
    setFavoriteIds((current) => {
      const next = current.filter((id) => liveMatchIds.has(String(id)));
      if (next.length !== current.length) {
        window.localStorage.setItem(favoriteStorageKey, JSON.stringify(next));
      }
      return next;
    });
  }, [liveMatchIds]);

  const toggleFavorite = (match) => {
    const matchId = String(match.fixtureId || match.id || match._id);
    setFavoriteIds((current) => {
      const next = current.includes(matchId)
        ? current.filter((id) => id !== matchId)
        : [matchId, ...current];
      window.localStorage.setItem(favoriteStorageKey, JSON.stringify(next));
      return next;
    });
  };

  const favoriteMatches = sortAutomaticLiveAnalysisFirst(
    liveMatches.filter((match) =>
      favoriteIds.includes(String(match.fixtureId || match.id || match._id))
    )
  );
  const regularMatches = sortAutomaticLiveAnalysisFirst(
    liveMatches.filter((match) =>
      !favoriteIds.includes(String(match.fixtureId || match.id || match._id))
    )
  );
  const prioritizedMatches = [...regularMatches].sort((a, b) => {
  const aMomentum = Number(a.liveAnalysis?.momentum || 0);
  const bMomentum = Number(b.liveAnalysis?.momentum || 0);

  const aGoalSignal = Boolean(a.liveAnalysis?.goalSignal);
  const bGoalSignal = Boolean(b.liveAnalysis?.goalSignal);

  if (aGoalSignal && !bGoalSignal) return -1;
  if (!aGoalSignal && bGoalSignal) return 1;

  return bMomentum - aMomentum;
});
  const actionableMatches = sortAutomaticLiveAnalysisFirst(liveMatches.filter(isStrictPlayableLiveMatch));
  const regularActionableMatches = prioritizedMatches.filter(isStrictPlayableLiveMatch);
  const cautiousMatches = regularMatches.filter((match) => !isStrictPlayableLiveMatch(match));
  const featured = favoriteMatches.length
    ? favoriteMatches.slice(0, 3)
    : (actionableMatches.length ? actionableMatches.slice(0, 3) : sortAutomaticLiveAnalysisFirst(liveMatches).slice(0, 3));
  const adminLiveDebugVisible = isAdmin && (liveDebug?.errorCode || liveDebug?.providerStatus || liveDebug?.preview);
  const renderLiveMatchCard = (match, keyPrefix, forceFavorite = false) => {
    const fixtureKey = String(match.fixtureId || match.id || match._id);

    return (
      <div
        key={`${keyPrefix}-${fixtureKey}`}
        data-live-fixture-id={fixtureKey}
        className={`live-signal-target ${highlightedFixtureId === fixtureKey ? 'live-signal-target-active' : ''}`}
      >
        <MatchCard
          match={match}
          isLive={true}
          isFavorite={forceFavorite || favoriteIds.includes(String(match.fixtureId || match.id || match._id))}
          onToggleFavorite={toggleFavorite}
        />
      </div>
    );
  };

  if (loading) return <div className="loading">Yukleniyor...</div>;

  return (
    <div className="page live-matches live-dashboard-page live-terminal-v3">
      <section className="hero-panel compact live-dashboard-hero-panel">
        <div className="hero-copy live-hero-copy">
          <h1 className="live-hero-title">
            CANLI TAKIP
            <br />
            VE
            <br />
            ANALIZ
          </h1>
        </div>
      </section>

      <section className="content-panel live-overview-panel">
        <div className="live-overview-strip">
          <div className="live-overview-box">
            <strong>{liveMatches.length}</strong>
            <span>Canli karsilasma</span>
          </div>
          <div className="live-overview-box">
            <strong>{favoriteMatches.length}</strong>
            <span>Takip edilen premium mac</span>
          </div>
          <div className="live-overview-box premium">
            <strong>{actionableMatches.length}</strong>
            <span>Guclu canli dashboard firsati</span>
          </div>
        </div>

        {featured.length ? (
          <div className="live-spotlight-grid">
            {featured.map((match) => (
              <div className="live-spotlight-card" key={`spotlight-${match.id || match._id}`}>
                <span className="league">{match.league?.name}</span>
                <strong>{match.homeTeam?.name} - {match.awayTeam?.name}</strong>
                <p>
                  {match.status?.elapsed || 0}' dakika - Skor {match.goals?.home || 0}:{match.goals?.away || 0} - Populerlik {Math.round(match.predictionContext?.popularityScore || match.popularityScore || 0)}
                </p>
              </div>
            ))}
          </div>
        ) : null}
      </section>

      {(liveNotice || liveWarning) ? (
        <section className="content-panel ai-admin-debug-panel">
          <div className="analysis-header">
            <span>Canli Veri Durumu</span>
            <span className="confidence high">{liveMatches.length > 0 ? 'Cache / Akis Hazir' : 'Tekrar Denenecek'}</span>
          </div>
          <div className="ai-admin-debug-grid">
            <div className="ai-admin-debug-item">
              <span>Kullanici Mesaji</span>
              <strong>{liveWarning || liveNotice}</strong>
            </div>
            {adminLiveDebugVisible ? (
              <>
                <div className="ai-admin-debug-item">
                  <span>Error Code</span>
                  <strong>{liveDebug?.errorCode || '-'}</strong>
                </div>
                <div className="ai-admin-debug-item">
                  <span>Provider Status</span>
                  <strong>{liveDebug?.providerStatus ?? '-'}</strong>
                </div>
                <div className="ai-admin-debug-item">
                  <span>Preview</span>
                  <strong>{liveDebug?.preview || '-'}</strong>
                </div>
              </>
            ) : null}
          </div>
        </section>
      ) : null}

      {favoriteMatches.length ? (
        <section className="content-panel live-follow-panel live-matches-section">
          <div className="section-heading">
            <div>
              <h2>Takip Ettiklerim</h2>
              <p>Yildiza bastigin canli maclar burada ustte sabit kalir. Premium dashboard once bu maclari one cikarir.</p>
            </div>
            <div className="section-chip">{favoriteMatches.length} mac</div>
          </div>
          <div className="matches-grid live-grid live-match-grid live-matches-grid">
            {favoriteMatches.map((match) => renderLiveMatchCard(match, 'favorite', true))}
          </div>
        </section>
      ) : null}

      <div className="section-heading">
        <div>
          <h2>Canli Analiz</h2>
          <p className="live-priority-notice">Goal signal, momentum, canlı skor, xG baskısı ve risk filtresi aynı kartta izlenir.</p>
        </div>
        <div className="live-meta">
          {refreshingLive ? <div className="live-refresh-badge">Canli guncelleniyor</div> : null}
          <div className="section-chip">{liveMatches.length} mac</div>
          <Link to="/analysis" className="section-link">Premium Analiz Odasi</Link>
        </div>
      </div>

      {liveMatches.length === 0 ? (
        <div className="empty">{liveNotice || 'Su anda canli mac bulunmuyor.'}</div>
      ) : (
        <>
          {regularActionableMatches.length ? (
            <section className="content-panel live-follow-panel live-matches-section">
              <div className="section-heading">
                <div>
                  <h2>Oncelikli Premium Dashboardlar</h2>
                  <p>Buyuk lig, yuksek veri kalitesi ve guclu canli guven skoru tasiyan maclar burada yer alir.</p>
                </div>
                <div className="section-chip">{regularActionableMatches.length} mac</div>
              </div>
              <div className="matches-grid live-grid live-match-grid live-matches-grid">
                {regularActionableMatches.map((match) => renderLiveMatchCard(match, 'actionable'))}
              </div>
            </section>
          ) : null}

          {cautiousMatches.length ? (
            <section className="content-panel live-follow-panel live-matches-section">
              <div className="section-heading">
                <div>
                  <h2>Dengeli Izleme Alani</h2>
                </div>
                <div className="section-chip">{cautiousMatches.length} mac</div>
              </div>
              <div className="matches-grid live-grid live-match-grid live-matches-grid">
                {cautiousMatches.map((match) => renderLiveMatchCard(match, 'cautious'))}
              </div>
            </section>
          ) : null}
        </>
      )}
    </div>
  );
};


export default LiveMatches;
